import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import type { Package, PackageInput } from "@/hooks/useAdmin";

interface PackageModalProps {
  open: boolean;
  onClose: () => void;
  packageItem: Package | null;
  onSaved: () => void;
  onSubmit: (input: PackageInput) => Promise<string | null>;
}

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors";
const labelClass = "block text-sm font-semibold text-foreground-800 mb-1.5";

export default function PackageModal({ open, onClose, packageItem, onSaved, onSubmit }: PackageModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [featuresText, setFeaturesText] = useState("");
  const [active, setActive] = useState(true);
  const [options,setOptions]=useState({annualEnabled:true,annualDiscountPercent:5,merakliEnabled:false,meetingEnabled:false,voiceCoach:false,parentalControls:false,offlineMemory:false});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setOptions({annualEnabled:packageItem?.annualEnabled??true,annualDiscountPercent:packageItem?.annualDiscountPercent??5,
        merakliEnabled:packageItem?.merakliEnabled??false,meetingEnabled:packageItem?.meetingEnabled??false,
        voiceCoach:packageItem?.voiceCoach??false,parentalControls:packageItem?.parentalControls??false,offlineMemory:packageItem?.offlineMemory??false});
      if (packageItem) {
        setName(packageItem.name);
        setDescription(packageItem.description);
        setPrice(String(packageItem.price || ""));
        setDurationDays(String(packageItem.durationDays || ""));
        setFeaturesText(packageItem.features.join("\n"));
        setActive(packageItem.active);
      } else {
        setName("");
        setDescription("");
        setPrice("");
        setDurationDays("");
        setFeaturesText("");
        setActive(true);
      }
      setError("");
    }
  }, [open, packageItem]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Paket adı zorunludur.");
      return;
    }

    const features = featuresText
      .split("\n")
      .map((f) => f.trim())
      .filter(Boolean);

    setSaving(true);
    const err = await onSubmit({
      name: name.trim(),
      description: description.trim(),
      price: price ? Number(price) : 0,
      durationDays: durationDays ? Number(durationDays) : 0,
      features,
      active,
      ...options,
    });
    setSaving(false);

    if (err) {
      setError(err);
    } else {
      onSaved();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={packageItem ? "Paketi Düzenle" : "Yeni Paket"}
      titleId="package-modal-title"
      id="package-modal"
      maxWidthClass="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="pkg-name" className={labelClass}>
            Paket Adı <span className="text-accent-600">*</span>
          </label>
          <input
            id="pkg-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ör. LGS Hazırlık, TYT-AYT Pro..."
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="pkg-desc" className={labelClass}>
            Açıklama
          </label>
          <textarea
            id="pkg-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Kısa açıklama"
            className={`${inputClass} h-auto py-2 resize-none`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="pkg-price" className={labelClass}>
              Aylık Fiyat (₺)
            </label>
            <input
              id="pkg-price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
              className={inputClass}
            />
          </div>
          <div>
            <p className={labelClass}>Erişim süresi</p>
            <p className="text-sm text-foreground-600">Aylık: 1 takvim ayı<br/>Yıllık peşin: 12 ay</p>
          </div>
        </div>

        <fieldset className="space-y-3 rounded-lg border p-3">
          <legend className="text-sm font-semibold">Ödeme ve uygulama erişimleri</legend>
          {([['annualEnabled','Yıllık peşin ödeme'],['merakliEnabled','Meraklı erişimi'],['meetingEnabled','Toplantı erişimi'],['voiceCoach','Sesli rehberlik'],['parentalControls','Veli uzaktan kontrolleri'],['offlineMemory','Yerel ders hafızası']] as const).map(([key,label])=>(
            <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={options[key]} onChange={e=>setOptions({...options,[key]:e.target.checked})}/>{label}</label>
          ))}
          <label className={labelClass} htmlFor="pkg-discount">12 aylık peşin ödeme indirimi (%)</label>
          <input id="pkg-discount" type="number" min="0" max="100" step="0.01" className={inputClass} value={options.annualDiscountPercent} onChange={e=>setOptions({...options,annualDiscountPercent:Number(e.target.value)})}/>
          <p className="text-xs">Yıllık tutar: {(Math.round(Number(price||0)*100)*12*(100-options.annualDiscountPercent)/10000).toLocaleString('tr-TR',{minimumFractionDigits:2,maximumFractionDigits:2})} TL. İzinler mevcut üyelerin sonraki yetki kontrolünde de uygulanır.</p>
        </fieldset>
        <div>
          <label htmlFor="pkg-features" className={labelClass}>
            Özellikler (her satıra bir özellik)
          </label>
          <textarea
            id="pkg-features"
            value={featuresText}
            onChange={(e) => setFeaturesText(e.target.value)}
            rows={4}
            placeholder={"Video dersler\nCanlı dersler\nSoru bankası"}
            className={`${inputClass} h-auto py-2 resize-none`}
          />
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="w-4 h-4 rounded accent-[var(--primary-500)] cursor-pointer"
          />
          <span className="text-sm text-foreground-800">Aktif (görünür)</span>
        </label>

        {error && (
          <div className="rounded-md bg-accent-100/70 text-accent-800 px-3 py-2.5 text-sm">{error}</div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                Kaydediliyor...
              </>
            ) : (
              "Kaydet"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}